import React, { useState, useEffect } from 'react';
import { Calendar, X, Save, Mail, Bell } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';

const STATUSES = ['Aangevraagd', 'Gepland', 'Bevestigd', 'In uitvoering', 'Klaar', 'Geannuleerd'];
const WERKSTATUSSEN = ['Niet gestart', 'Wacht op onderdelen', 'Bezig', 'Klaar', 'Opgehaald'];
const DUUR_OPTIES = [15, 30, 45, 60, 90, 120, 180, 240, 480];

// Lokale datetime-string voor <input type="datetime-local">. toISOString() shifts naar UTC
// waardoor de defaultwaarde 1-2u eerder zou staan dan de wandklok.
const localDateTime = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
};

export default function PlanningEditor({ afspraak, onClose }) {
  const { t } = useT();
  const { klanten, gegevens, createPlanning, updatePlanning, notifyPlanning } = useData();
  const isNew = !afspraak || !afspraak.id;
  const [team, setTeam] = useState([]);
  useEffect(() => {
    carsysApi.getTeam().then(setTeam).catch(() => setTeam([]));
  }, []);
  const werkplekken = (gegevens?.locaties && gegevens.locaties.length) ? gegevens.locaties
    : (gegevens?.werkplekken && gegevens.werkplekken.length) ? gegevens.werkplekken
    : ['Brug 1', 'Brug 2'];
  // Monteurs = unieke team-namen + reeds gebruikte monteurs in deze afspraak (zodat oude waarde
  // niet verdwijnt als die persoon niet meer in team zit).
  const monteurNamen = React.useMemo(() => {
    const teamNamen = team.map(m => m.naam || m.email).filter(Boolean);
    const existing = afspraak?.monteur ? [afspraak.monteur] : [];
    return Array.from(new Set([...teamNamen, ...existing]));
  }, [team, afspraak]);
  const defaultMonteur = monteurNamen[0] || '';

  const [data, setData] = useState(() => afspraak || {
    titel: '', klantId: '', kenteken: '',
    start: localDateTime(new Date()),
    eind: localDateTime(new Date(Date.now() + 60 * 60000)),
    status: 'Gepland', werkStatus: 'Niet gestart', werkplek: '', monteur: '', notitie: ''
  });

  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  const addMinutes = (dtLocal, mins) => {
    const d = new Date(dtLocal); d.setMinutes(d.getMinutes() + mins); return localDateTime(d);
  };
  const curDuur = Math.max(0, Math.round((new Date(data.eind) - new Date(data.start)) / 60000)) || 60;
  // Start wijzigen: behoud de huidige duur (verschuif eind mee)
  const onStartChange = (v) => setData(d => ({ ...d, start: v, eind: addMinutes(v, Math.max(15, Math.round((new Date(d.eind) - new Date(d.start)) / 60000) || 60)) }));
  // Duur kiezen: zet eind = start + duur, bewaar geschatteMinuten
  const onDuurChange = (mins) => setData(d => ({ ...d, geschatteMinuten: mins, eind: addMinutes(d.start, mins) }));

  // Effectieve monteur = expliciet gekozen óf de default uit het team (zonder setState in effect)
  const effectieveMonteur = data.monteur || defaultMonteur || '';

  const handleSave = async () => {
    if (!data.titel.trim()) { toast.error(t('Vul een titel in')); return; }
    try {
      const payload = { ...data, monteur: effectieveMonteur };
      if (isNew) await createPlanning(payload);
      else await updatePlanning(data.id, payload);
      toast.success(t('Afspraak opgeslagen'));
      onClose();
    } catch { toast.error(t('Opslaan mislukt')); }
  };

  const handleNotify = async (kind) => {
    if (isNew) { toast.error(t('Sla eerst de afspraak op')); return; }
    const klant = klanten.find(k => k.id === data.klantId);
    const aan = klant?.email || data.klantEmail;
    if (!aan) { toast.error(t('Klant heeft geen e-mailadres')); return; }
    const label = kind === 'herinnering' ? t('Herinnering verstuurd') : t('Bevestiging verstuurd');
    try {
      const res = await notifyPlanning(data.id, { kind, aan });
      const isReal = !String(res.status || '').toUpperCase().includes('MOCK');
      if (isReal) toast.success(label, { description: `${t('Aan')}: ${aan}` });
      else toast.warning(t('E-mail niet geconfigureerd'), { description: res.status });
    } catch (err) { toast.error(t('Versturen mislukt'), { description: err.response?.data?.detail || err.message }); }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}><Calendar size={16} style={{ verticalAlign: 'middle' }} /> {isNew ? t('Nieuwe afspraak') : t('Afspraak bewerken', 'Afspraak bewerken')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {data.bron === 'online' && (
            <div data-testid="planning-online-info" style={{ background: 'var(--cs-amber-soft, #fffbeb)', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>📅 {t('Online aanvraag', 'Online aanvraag')}{data.dienst ? ` — ${data.dienst}` : ''}</div>
              <div style={{ color: '#92400e' }}>
                {data.klantNaam || '-'}{data.klantTelefoon ? ` · ${data.klantTelefoon}` : ''}{data.klantEmail ? ` · ${data.klantEmail}` : ''}
              </div>
              {data.status === 'Aangevraagd' && <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 4 }}>{t('Zet de status op "Bevestigd" en klik op Bevestiging om de klant te informeren.', 'Zet de status op "Bevestigd" en klik op Bevestiging om de klant te informeren.')}</div>}
            </div>
          )}
          <div><label className="cs-section-title">{t('Titel')}</label><input className="cs-input" value={data.titel} onChange={e => upd('titel', e.target.value)} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label className="cs-section-title">{t('Klant')}</label>
              <select className="cs-input" value={data.klantId} onChange={e => { upd('klantId', e.target.value); const k = klanten.find(x => x.id === e.target.value); if (k && k.kentekens && k.kentekens[0]) upd('kenteken', k.kentekens[0]); }}>
                <option value="">-- {t('Kies')} --</option>
                {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
              </select>
            </div>
            <div><label className="cs-section-title">{t('Kenteken')}</label><input className="cs-input" value={data.kenteken} onChange={e => upd('kenteken', e.target.value.toUpperCase())} /></div>
            <div><label className="cs-section-title">{t('Start')}</label><input type="datetime-local" className="cs-input" value={data.start} onChange={e => onStartChange(e.target.value)} data-testid="planning-start" /></div>
            <div><label className="cs-section-title">{t('Geschatte duur', 'Geschatte duur')}</label>
              <select className="cs-input" value={curDuur} onChange={e => onDuurChange(Number(e.target.value))} data-testid="planning-duur">
                {DUUR_OPTIES.map(m => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} ${t('uur', 'uur')}${m % 60 ? ` ${m % 60}m` : ''}`}</option>)}
                {!DUUR_OPTIES.includes(curDuur) && <option value={curDuur}>{curDuur} min</option>}
              </select>
            </div>
            <div><label className="cs-section-title">{t('Werkplek / brug', 'Werkplek / brug')}</label>
              <select className="cs-input" value={data.werkplek || ''} onChange={e => upd('werkplek', e.target.value)} data-testid="planning-werkplek">
                <option value="">-- {t('Geen', 'Geen')} --</option>
                {werkplekken.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div><label className="cs-section-title">{t('Monteur')}</label>
              {monteurNamen.length > 0
                ? <select className="cs-input" value={data.monteur} onChange={e => upd('monteur', e.target.value)} data-testid="planning-monteur">
                    <option value="">-- {t('Geen monteur', 'Geen monteur')} --</option>
                    {monteurNamen.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                : <input className="cs-input" data-testid="planning-monteur" placeholder={t('Voeg eerst teamleden toe', 'Voeg eerst teamleden toe in Instellingen → Team')} value={data.monteur} onChange={e => upd('monteur', e.target.value)} />
              }
            </div>
            <div><label className="cs-section-title">{t('Status')}</label><select className="cs-input" value={data.status} onChange={e => upd('status', e.target.value)}>{STATUSES.map(s => <option key={s} value={s}>{t(s, s)}</option>)}</select></div>
            <div><label className="cs-section-title">{t('Werkstatus', 'Werkstatus')}</label>
              <select className="cs-input" value={data.werkStatus || 'Niet gestart'} onChange={e => upd('werkStatus', e.target.value)} data-testid="planning-werkstatus">
                {WERKSTATUSSEN.map(s => <option key={s} value={s}>{t(s, s)}</option>)}
              </select>
            </div>
          </div>
          <div><label className="cs-section-title">{t('Notitie')}</label><textarea className="cs-input" rows={2} value={data.notitie} onChange={e => upd('notitie', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {!isNew && <>
              <button className="cs-btn" onClick={() => handleNotify('bevestiging')}><Mail size={12} /> {t('Bevestiging', 'Bevestiging')}</button>
              <button className="cs-btn" onClick={() => handleNotify('herinnering')}><Bell size={12} /> {t('Herinnering', 'Herinnering')}</button>
            </>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
            <button className="cs-btn primary" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
          </div>
        </div>
      </div>
      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.55); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 620px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
      `}</style>
    </div>
  );
}
